'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import YoutubePlayIcon from '@repo/ui/src/components/Icons/YoutubePlayIcon';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { YoutubeLinkProps } from '@repo/ui/src/types';
import Image from '@repo/ui/src/components/Image';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { classNames } from '@repo/ui/src/utils/classNames';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';

interface Props {
  youtubeLink: YoutubeLinkProps;
}

const RecipeVideo = ({ youtubeLink }: Props) => {
  const { push } = useModal(ModalID.video);
  const { t } = useTranslation();

  if (!youtubeLink) return null;

  const youtubeLinkThumbnail = youtubeLink?.uploadedThumbnail
    ? buildImageSrc(youtubeLink.uploadedThumbnail[0], 200, 3, 2)
    : youtubeLink?.thumbnailUrl;

  const hasMedia = youtubeLink?.thumbnailVideo || youtubeLinkThumbnail;

  return (
    <div
      className={classNames(
        'w-full items-center border rounded border-warm-400 mb-8',
        hasMedia &&
          'min-h-[316px] sm:min-h-[284px] grid grid-rows-[1.8fr_1fr] sm:grid-rows-1 sm:grid-cols-[1.3fr_1fr] gap-1 sm:gap-4',
      )}
    >
      {hasMedia && (
        <div className="h-full relative w-full overflow-hidden">
          {youtubeLink.thumbnailVideo ? (
            <BackgroundVideo
              video={youtubeLink.thumbnailVideo}
              playButton={true}
              width={500}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          ) : (
            <Image
              src={youtubeLinkThumbnail!}
              alt={`${youtubeLink.name} thumbnail`}
              layout="fill"
              objectFit="cover"
            />
          )}
        </div>
      )}
      <div className={classNames(hasMedia ? 'p-4' : 'p-12')}>
        {youtubeLink.name && <p className="tra-text-web-heading-5">{youtubeLink.name}</p>}
        <Button
          variant={ButtonVariant.secondary}
          size="small"
          onClick={() => push({ youtubeLink })}
          aria-label="play button"
          className="mt-4 flex items-center"
        >
          <YoutubePlayIcon height="20px" className="h-full inline-flex align-sub" />
          <span className="ml-2">
            {t('recipeContent.watchVideoLabel', undefined, { default: 'Watch Video' })}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default RecipeVideo;
